import { getIdToken } from "./auth.js";
import type { Poll, PaginatedPolls, UserStats, CreatePollInput, UpdatePollInput } from "../types/index.js";

const API_URL = import.meta.env.VITE_API_URL || "";

async function getAuthHeaders(): Promise<Record<string, string>> {
    const token = await getIdToken();
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
    };

    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    return headers;
}

async function handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(error.error || `HTTP error ${response.status}`);
    }
    return response.json();
}

// User

export async function fetchCurrentUser(): Promise<UserStats> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/admin/me`, {
        method: "GET",
        headers,
    });
    return handleResponse<UserStats>(response);
}

// Polls

export async function fetchPolls(
    page = 1,
    limit = 20,
    includeDeleted = false
): Promise<PaginatedPolls> {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        includeDeleted: includeDeleted.toString(),
    });

    const response = await fetch(`${API_URL}/admin/polls?${params}`, {
        method: "GET",
        headers,
    });

    return handleResponse<PaginatedPolls>(response);
}

export async function fetchPoll(pollId: string): Promise<Poll> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/admin/polls/${pollId}`, {
        method: "GET",
        headers,
    });

    return handleResponse<Poll>(response);
}

export async function createPoll(input: CreatePollInput): Promise<Poll> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/admin/polls`, {
        method: "POST",
        headers,
        body: JSON.stringify(input),
    });

    return handleResponse<Poll>(response);
}

export async function updatePoll(pollId: string, input: UpdatePollInput): Promise<Poll> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/admin/polls/${pollId}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(input),
    });

    return handleResponse<Poll>(response);
}

export async function deletePoll(pollId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/admin/polls/${pollId}`, {
        method: "DELETE",
        headers,
    });

    await handleResponse<{ success: boolean }>(response);
}

export async function restorePoll(pollId: string): Promise<Poll> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/admin/polls/${pollId}/restore`, {
        method: "POST",
        headers,
    });

    const result = await handleResponse<{ success: boolean; poll: Poll }>(response);
    return result.poll;
}
